import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Mic2, Trophy, Users, ChevronRight, Zap, Shield, Target } from 'lucide-react';
import SigningDayCountdown from '../components/SigningDayCountdown';
import StreetScene from '../components/StreetScene';
import { SIGNING_DAY_LABEL } from '../config/signingDay';

const LOGO_URL = "/twlogo-portrait.png";
const HERO_BG = "https://images.unsplash.com/photo-1571575522341-be760794a35b?crop=entropy&cs=srgb&fm=jpg&q=85";
const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

const LandingPage = () => {
    const { isAuthenticated } = useAuth();
    const [spots, setSpots] = useState(null);
    const [locked, setLocked] = useState(null);

    useEffect(() => {
        axios.get(`${API}/stats`).then((r) => {
            setSpots(r.data?.total_artists ?? null);
            setLocked(r.data?.signing_day_locked ?? null);
        }).catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-void">
            {/* Hero — POV street-corner scene (the flyer taped to a streetlight) */}
            <StreetScene />

            {/* (retired text hero — kept hidden; StreetScene is the live hero) */}
            <section className="hidden">
                <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
                    {/* Recruitment notice kicker */}
                    <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-neon-blue/30 bg-neon-blue/5 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                        <span className="w-2 h-2 rounded-full bg-truth animate-pulse" />
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-neon-blue">Effortless Metaphor · Recruitment Notice</span>
                    </div>

                    {/* Logo */}
                    <img
                        src={LOGO_URL}
                        alt="TrapWorld"
                        className="w-40 h-40 sm:w-52 sm:h-52 mx-auto object-contain mb-2 animate-fade-in-up"
                        style={{ animationDelay: '0.2s' }}
                        data-testid="hero-logo"
                    />

                    {/* Headline */}
                    <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tighter mb-5 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                        <span className="text-white">IN SEARCH OF</span><br />
                        <span className="text-neon-purple">NEW TALENT</span>
                    </h1>
                    <div className="mx-auto mb-6 h-px w-32 opacity-60" style={{ background: 'linear-gradient(90deg,transparent,#FFE000,transparent)' }} />

                    {/* Flyer body */}
                    <p className="text-lg sm:text-xl text-zinc-300 max-w-2xl mx-auto mb-2 animate-fade-in-up" style={{ animationDelay: '0.35s' }}>
                        All costs covered. Claim your spot — a room outfitted with everything you need: the studio, the bars, the beats.
                    </p>
                    <p className="text-base text-zinc-400 max-w-2xl mx-auto mb-10 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                        Train now. <span className="text-white font-semibold">Drop your track on Signing Day</span> — the one day real deals get done. Earned on the mic. Never bought.
                    </p>

                    {/* Countdown */}
                    <div className="mb-8 animate-fade-in-up" style={{ animationDelay: '0.45s' }}>
                        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
                            Signing Day · {SIGNING_DAY_LABEL}
                        </div>
                        <SigningDayCountdown />
                    </div>

                    {/* CTA + live signups counter */}
                    <div className="flex flex-col items-center gap-5 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                        {isAuthenticated ? (
                            <Link to="/practice" data-testid="cta-room">
                                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-black uppercase tracking-widest shadow-glow-purple rounded-sm px-10 py-6 text-base">
                                    Enter Your Room <ChevronRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                        ) : (
                            <Link to="/register" data-testid="cta-claim">
                                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-black uppercase tracking-widest shadow-glow-purple rounded-sm px-10 py-6 text-base">
                                    Claim Your Spot <ChevronRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                        )}
                        <div className="font-mono text-sm text-zinc-400 flex flex-col items-center gap-1">
                            <span>
                                <span className="text-truth font-bold">{spots == null ? '—' : spots}</span>{' '}
                                {spots === 1 ? 'spot' : 'spots'} claimed · all costs included
                            </span>
                            {locked != null && locked > 0 && (
                                <span>
                                    <span className="text-neon-blue font-bold">{locked}</span>{' '}
                                    {locked === 1 ? 'track' : 'tracks'} locked in for Signing Day
                                </span>
                            )}
                        </div>
                        <a href="#how" className="font-mono text-xs uppercase tracking-widest text-zinc-500 hover:text-white transition-colors">
                            What is Signing Day? ↓
                        </a>
                    </div>
                </div>

            </section>

            {/* Philosophy Section */}
            <section className="py-24 px-4 bg-paper">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4">
                            THE <span className="text-neon-purple">DOCTRINE</span>
                        </h2>
                        <p className="text-zinc-400 max-w-xl mx-auto">
                            If you're respected here, you earned it.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { icon: Target, title: 'Skill > Popularity', desc: 'Your ranking reflects your craft, not your followers', color: '#00E5FF' },
                            { icon: Mic2, title: 'Performance > Persona', desc: 'Let your bars speak. Image means nothing here.', color: '#FF3DCB' },
                            { icon: Shield, title: 'Consistency > Virality', desc: 'Build reputation over time. No one-hit wonders.', color: '#8B5CFF' },
                            { icon: Users, title: 'Community > Algorithms', desc: 'Real judges. Structured feedback. Earned respect.', color: '#39FF14' },
                        ].map((item, i) => (
                            <div
                                key={item.title}
                                className="candy-card rounded-2xl p-6 hover:scale-[1.02] transition-all duration-300 group"
                                data-testid={`doctrine-${i}`}
                            >
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors relative z-10" style={{background:`${item.color}18`}}>
                                    <item.icon className="w-6 h-6" style={{color: item.color}} />
                                </div>
                                <h3 className="font-display font-bold text-base mb-2 relative z-10">{item.title}</h3>
                                <p className="text-sm text-zinc-400 relative z-10">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section id="how" className="py-24 px-4 scroll-mt-20">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-neon-blue mb-3">What is Signing Day</div>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4">
                            CLAIM YOUR SPOT. <span className="text-neon-purple">TRAIN.</span> GET SIGNED.
                        </h2>
                        <p className="text-zinc-400 max-w-2xl mx-auto">
                            Sign up free and your room is outfitted with everything you need. Sharpen your bars in private,
                            then on <span className="text-white">Signing Day</span> you drop your track into the Arena — the one
                            day Effortless Metaphor and special guests are signing real artists. Earned on the mic, never bought.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { step: '01', title: 'Upload',     desc: 'Drop your best bars. Acapella, over beats, freestyles—show what you got.', icon: Mic2,   color: '#00E5FF' },
                            { step: '02', title: 'Get Judged', desc: 'Community rates your Flow, Cadence, Delivery, and Clarity. No lazy likes.', icon: Users,  color: '#FF3DCB' },
                            { step: '03', title: 'Rise',       desc: 'Earn your tier. Rookie to Legend. Consistency is king.',                    icon: Trophy, color: '#39FF14' },
                        ].map((item, i) => (
                            <div key={item.step} className="relative" data-testid={`step-${item.step}`}>
                                <div className="candy-card rounded-2xl p-8 h-full hover:scale-[1.02] transition-all duration-300">
                                    <span className="font-mono text-5xl font-bold text-white/5 absolute top-4 right-4 z-10">{item.step}</span>
                                    <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 relative z-10" style={{background:`${item.color}18`}}>
                                        <item.icon className="w-7 h-7" style={{color: item.color}} />
                                    </div>
                                    <h3 className="font-display font-bold text-xl mb-3 relative z-10">{item.title}</h3>
                                    <p className="text-zinc-400 relative z-10">{item.desc}</p>
                                </div>
                                {i < 2 && (
                                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                                        <ChevronRight className="w-8 h-8 text-white/20" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Tier System */}
            <section className="py-24 px-4 bg-paper">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4">
                            THE <span className="text-[#7FFF00]">RANKS</span>
                        </h2>
                        <p className="text-zinc-400 max-w-xl mx-auto">
                            Progression is earned. Every tier tells a story.
                        </p>
                    </div>

                    <div className="space-y-3">
                        {[
                            { name: 'LEGEND',    score: '2000+', cls: 'tier-legend', glow: 'shadow-glow-green' },
                            { name: 'ELITE',     score: '1000+', cls: 'tier-elite' },
                            { name: 'PRO',       score: '600+',  cls: 'tier-pro' },
                            { name: 'PROSPECT',  score: '300+',  cls: 'tier-prospect' },
                            { name: 'CONTENDER', score: '100+',  cls: 'tier-contender' },
                            { name: 'ROOKIE',    score: '0+',    cls: 'tier-rookie' },
                        ].map((tier, i) => (
                            <div
                                key={tier.name}
                                className={`flex items-center justify-between p-4 rounded-xl candy-card hover:scale-[1.01] transition-all duration-200 ${tier.glow || ''}`}
                                data-testid={`tier-${tier.name.toLowerCase()}`}
                            >
                                <div className="flex items-center gap-4 relative z-10">
                                    <span className="font-mono text-sm text-zinc-600 w-6">{6 - i}</span>
                                    <span className={`px-3 py-1 rounded-full font-mono text-xs font-bold uppercase ${tier.cls}`}>
                                        {tier.name}
                                    </span>
                                </div>
                                <span className="font-mono text-sm text-zinc-400 relative z-10">{tier.score} pts</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 px-4 relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-30" />
                <div className="relative z-10 max-w-2xl mx-auto text-center">
                    <Zap className="w-16 h-16 mx-auto mb-6 text-truth" style={{filter:'drop-shadow(0 0 12px rgba(57,255,20,.5))'}} />
                    <h2 className="font-display font-black text-3xl sm:text-4xl mb-6">
                        THE GATE CLOSES ON <span className="text-neon-purple">SIGNING DAY</span>.
                    </h2>
                    <p className="text-zinc-400 mb-8">
                        Spots are free, but the clock is real. Claim yours, get outfitted, and have your track ready when the doors open.
                    </p>
                    <div className="mb-8 flex justify-center">
                        <SigningDayCountdown />
                    </div>
                    {!isAuthenticated && (
                        <Link to="/register" data-testid="cta-final-join">
                            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-widest shadow-glow-purple rounded-sm px-10">
                                Claim Your Spot
                                <ChevronRight className="w-5 h-5 ml-2" />
                            </Button>
                        </Link>
                    )}
                </div>
            </section>
        </div>
    );
};

export default LandingPage;
